import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icons } from './Icons';

const MOBILE_MQ = '(max-width: 599px)';
const FINE_MQ = '(pointer: fine)';

const useMediaQuery = (query) => {
	const [matches, setMatches] = useState(() => {
		if (typeof window === 'undefined' || !window.matchMedia) return false;
		return window.matchMedia(query).matches;
	});
	useEffect(() => {
		if (typeof window === 'undefined' || !window.matchMedia) return;
		const mql = window.matchMedia(query);
		const handler = (e) => setMatches(e.matches);
		mql.addEventListener ? mql.addEventListener('change', handler) : mql.addListener(handler);
		setMatches(mql.matches);
		return () => {
			mql.removeEventListener ? mql.removeEventListener('change', handler) : mql.removeListener(handler);
		};
	}, [query]);
	return matches;
};

const renderBody = (content) => {
	if (!content) return null;
	if (typeof content === 'string') {
		return <p className='pn-help-body'>{content}</p>;
	}
	const { title, items, text } = content;
	return (
		<>
			{title && <p className='pn-help-title'>{title}</p>}
			{text && <p className='pn-help-body'>{text}</p>}
			{Array.isArray(items) && items.length > 0 && (
				<ul className='pn-help-list'>
					{items.map((item, i) => <li key={i}>{item}</li>)}
				</ul>
			)}
		</>
	);
};

export const HelpPopover = ({ content, label, className }) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [coords, setCoords] = useState(null);
	const [placement, setPlacement] = useState('top');
	const [rowEl, setRowEl] = useState(null);
	const [groupColor, setGroupColor] = useState(null);
	const triggerRef = useRef(null);
	const popoverRef = useRef(null);
	const inlineRef = useRef(null);
	const isMobile = useMediaQuery(MOBILE_MQ);
	const isFinePointer = useMediaQuery(FINE_MQ);
	const popoverId = useId();

	useEffect(() => {
		if (triggerRef.current) {
			setRowEl(triggerRef.current.closest('.pn-score-kpi') || triggerRef.current.parentElement);
			const group = triggerRef.current.closest('.pn-score-group');
			if (group) {
				const c = getComputedStyle(group).getPropertyValue('--pn-group-color').trim();
				if (c) setGroupColor(c);
			}
		}
	}, []);

	const computeCoords = useCallback(() => {
		const trigger = triggerRef.current;
		const pop = popoverRef.current;
		if (!trigger || !pop) return;
		const triggerRect = trigger.getBoundingClientRect();
		const popRect = pop.getBoundingClientRect();
		const vw = window.innerWidth;
		const vh = window.innerHeight;
		const margin = 8;
		const gap = 8;

		const spaceBelow = vh - triggerRect.bottom;
		const spaceAbove = triggerRect.top;
		const fitsBelow = spaceBelow >= popRect.height + gap + margin;
		const placeTop = !fitsBelow && spaceAbove >= popRect.height + gap + margin;

		const top = placeTop
			? triggerRect.top - popRect.height - gap
			: triggerRect.bottom + gap;

		const triggerCenter = triggerRect.left + triggerRect.width / 2;
		let left = triggerCenter - popRect.width / 2;
		left = Math.max(margin, Math.min(left, vw - popRect.width - margin));

		const arrowLeft = triggerCenter - left;
		setPlacement(placeTop ? 'top' : 'bottom');
		setCoords({ top, left, arrowLeft });
	}, []);

	useEffect(() => {
		if (!open || isMobile) return;
		computeCoords();
		const onScroll = () => setOpen(false);
		const onResize = () => computeCoords();
		window.addEventListener('scroll', onScroll, true);
		window.addEventListener('resize', onResize);
		return () => {
			window.removeEventListener('scroll', onScroll, true);
			window.removeEventListener('resize', onResize);
		};
	}, [open, isMobile, computeCoords]);

	useEffect(() => {
		if (!open) return;
		const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
		const onPointerDown = (e) => {
			// On mobile the panel is inline (not floating) so clicking outside
			// to close would interfere with normal scroll/tap interactions.
			// Closing is done by tapping the trigger (?) again.
			if (isMobile) return;
			const t1 = triggerRef.current;
			const p1 = popoverRef.current;
			if (t1?.contains(e.target)) return;
			if (p1?.contains(e.target)) return;
			setOpen(false);
		};
		document.addEventListener('keydown', onKey);
		document.addEventListener('pointerdown', onPointerDown, true);
		return () => {
			document.removeEventListener('keydown', onKey);
			document.removeEventListener('pointerdown', onPointerDown, true);
		};
	}, [open, isMobile]);

	const toggle = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setOpen((v) => !v);
	};

	const onTriggerKey = (e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			setOpen((v) => !v);
		}
	};

	const handleEnter = () => {
		if (!isFinePointer || isMobile) return;
		setOpen(true);
	};
	const handleLeave = (e) => {
		if (!isFinePointer || isMobile) return;
		const next = e.relatedTarget;
		if (next && (popoverRef.current?.contains(next) || triggerRef.current?.contains(next))) return;
		setOpen(false);
	};

	const triggerProps = {
		ref: triggerRef,
		type: 'button',
		className: `pn-help-trigger${className ? ` ${className}` : ''}`,
		'aria-label': label || t('payload.payloadScore.helpAria', { defaultValue: 'More info' }),
		'aria-expanded': open,
		'aria-haspopup': 'dialog',
		'aria-controls': open ? popoverId : undefined,
		onClick: toggle,
		onKeyDown: onTriggerKey,
		onMouseEnter: handleEnter,
		onMouseLeave: handleLeave,
	};

	const popoverStyle = {
		...(coords ? { top: coords.top, left: coords.left, visibility: 'visible' } : { visibility: 'hidden' }),
		...(groupColor ? { '--pn-group-color': groupColor } : null),
	};
	const desktopPopover = open && !isMobile && (
		<div
			ref={popoverRef}
			id={popoverId}
			role='dialog'
			className={`pn-help-popover pn-help-popover--${placement}`}
			style={popoverStyle}
			onMouseEnter={handleEnter}
			onMouseLeave={handleLeave}
		>
			<div className='pn-help-popover-inner'>
				{renderBody(content)}
			</div>
			<span
				className='pn-help-popover-arrow'
				style={coords ? { left: coords.arrowLeft } : undefined}
				aria-hidden='true'
			/>
		</div>
	);

	const mobileInline = open && isMobile && rowEl && (
		<div
			ref={inlineRef}
			id={popoverId}
			role='region'
			aria-label={label || t('payload.payloadScore.helpAria', { defaultValue: 'More info' })}
			className='pn-help-inline'
		>
			<div className='pn-help-inline-body'>
				{renderBody(content)}
			</div>
		</div>
	);

	return (
		<>
			<button {...triggerProps}>
				<Icons.help />
			</button>
			{typeof document !== 'undefined' && !isMobile && createPortal(desktopPopover, document.body)}
			{rowEl && mobileInline && createPortal(mobileInline, rowEl)}
		</>
	);
};

HelpPopover.propTypes = {
	content: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.shape({
			title: PropTypes.string,
			text: PropTypes.string,
			items: PropTypes.arrayOf(PropTypes.node),
		}),
	]).isRequired,
	label: PropTypes.string,
	className: PropTypes.string,
};

export default { HelpPopover };
